import React from "react";
import {
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
    Grid,
    TextField,
    Tooltip,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

import {
    ChevronLeft,
    ChevronRightOutlined,
    HomeOutlined,
    Groups2Outlined,
    PublicOutlined,
} from "@mui/icons-material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import { FaPeopleRoof, FaFileContract } from "react-icons/fa6";
import { GiExpense } from "react-icons/gi";
import { RiCommunityFill } from "react-icons/ri";
import { FaSortAmountUp, FaSortAmountDown } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { BiSolidReport } from "react-icons/bi";
import { TbReportSearch } from "react-icons/tb";
import { getAllDashboardData } from "state/apis";
import { setPropertyAsDefault, getDefaultProperty } from "state/index";
import { toast } from "react-toastify";
import { designTokens } from "theme";

const navItems = [
    { text: "Dashboard", icon: <HomeOutlined /> },
    { text: "Report", icon: <BiSolidReport size={18} /> },
    { text: "Position reports", icon: <BiSolidReport size={18} /> },
    { text: "Search", icon: <TbReportSearch size={18} /> },
    { text: "Contract", icon: <FaFileContract size={16} /> },
    { text: "Receipts", icon: <FaSortAmountUp size={16} /> },
    { text: "Expenses", icon: <FaSortAmountDown size={16} /> },
    { text: "Common Expense", icon: <TbReportSearch size={18} /> },
    { text: "Voucher Receipts", icon: <BiSolidReport size={18} /> },
    { text: "Payment Voucher", icon: <BiSolidReport size={18} /> },
    { text: "Rent Dispute", icon: <BiSolidReport size={18} /> },
    { text: "Owners", icon: <Groups2Outlined /> },
    { text: "Properties", icon: <PublicOutlined /> },
    { text: "Sold Properties", icon: <PublicOutlined /> },
    { text: "Units", icon: <RiCommunityFill size={18} /> },
    { text: "Expense Types", icon: <GiExpense size={18} /> },
    { text: "Tenants", icon: <FaPeopleRoof size={18} /> },
    { text: "Employee", icon: <FaPeopleRoof size={18} /> },
    { text: "Roles", icon: <Groups2Outlined /> },
    { text: "Users", icon: <Groups2Outlined /> },
];

const sidebarColors = designTokens.sidebar;

const Sidebar = ({ user, drawerWidth, isSidebarOpen, setIsSidebarOpen, isNonMobile }) => {
    const { pathname } = useLocation();
    const [active, setActive] = useState("");
    const [property, setProperty] = useState(null);
    const [allProperty, setAllProperty] = useState([]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [role, setRole] = useState({});

    useEffect(() => { fetchData(); fetchProperty(); }, []);

    useEffect(() => {
        const decodedPath = decodeURIComponent(pathname.substring(1));
        setActive(decodedPath);
        fetchData();
        fetchProperty();
    }, [pathname]);

    const fetchData = async () => {
        const res = await getDefaultProperty(dispatch);
        if (res.data) setProperty(res.data?.property);
        if (res?.data?.role && res?.data?.role?.permission) setRole(JSON.parse(res?.data?.role?.permission));
    };

    const fetchProperty = async () => {
        const res = await getAllDashboardData();
        setAllProperty(res?.data?.properties || []);
    };

    const setDefaultProperty = async (id) => {
        try {
            await setPropertyAsDefault(dispatch, id);
            const res = await getDefaultProperty(dispatch);
            setProperty(res?.data?.property);
            toast.success("Default property updated successfully!");
            window.location.reload();
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to update default property. Please try again.");
        }
    };

    const handlePropertyChange = (newValue) => { if (newValue) setDefaultProperty(newValue.id); };

    const hasViewPermission = (item) => {
        const permissionMapping = {
            dashboard: true, roles: role?.role?.view, users: role?.user?.view,
            owners: role?.owner?.view, properties: role?.property?.view, units: role?.unit?.view,
            "expense types": role?.expenseType?.view, tenants: role?.tenant?.view,
            contract: role?.contract?.view, receipts: role?.receipt?.view,
            expenses: role?.expense?.view, "common expense": role?.commonExpense?.view,
            search: role?.search?.view, report: role?.report?.view,
            "voucher receipts": role?.voucherReceipt?.view, "payment voucher": role?.voucherReceipt?.view,
            "rent dispute": role?.voucherReceipt?.view, employee: role?.employee?.view,
            "position reports": role?.snapshot?.view, "sold properties": role?.soldProperty?.view,
        };
        return permissionMapping[item.toLowerCase()];
    };

    return (
        <Box component="nav">
            {isSidebarOpen && (
                <Drawer
                    open={isSidebarOpen}
                    onClose={() => setIsSidebarOpen(false)}
                    variant="persistent"
                    anchor="left"
                    sx={{
                        width: drawerWidth,
                        "& .MuiDrawer-paper": {
                            backgroundColor: sidebarColors.bg,
                            boxSizing: "border-box",
                            borderRight: "none",
                            width: drawerWidth,
                            display: "flex",
                            flexDirection: "column",
                        },
                    }}
                >
                    <Box width="100%" sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        {/* Logo & Property */}
                        <Box sx={{ p: "20px 20px 14px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                            <FlexBetween>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#fff", lineHeight: 1.3 }}>
                                        Burgan Real Estate
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.6875rem", color: sidebarColors.textMuted, mt: 0.25 }}>
                                        Tenants Management System
                                    </Typography>
                                </Box>
                                {!isNonMobile && (
                                    <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)} sx={{ color: sidebarColors.text }}>
                                        <ChevronLeft />
                                    </IconButton>
                                )}
                            </FlexBetween>

                            <Grid item xs={12} sx={{ mt: 1.5 }}>
                                <Autocomplete
                                    disablePortal
                                    options={allProperty}
                                    getOptionLabel={(option) => option.propertyName}
                                    value={property}
                                    onChange={(event, newValue) => handlePropertyChange(newValue)}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id} style={{ color: option.isSold ? designTokens.error.main : "", fontSize: "0.8125rem" }}>
                                            {option.propertyName}
                                        </li>
                                    )}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "rgba(255,255,255,0.08)",
                                            borderRadius: "6px",
                                            fontSize: "0.8125rem",
                                            color: "#fff",
                                            "& fieldset": { borderColor: "rgba(255,255,255,0.15)" },
                                            "&:hover fieldset": { borderColor: "rgba(255,255,255,0.3)" },
                                            "&.Mui-focused fieldset": { borderColor: designTokens.primary.fixedDim, borderWidth: 1.5 },
                                        },
                                        "& .MuiInputLabel-root": { fontSize: "0.8125rem", color: sidebarColors.textMuted, "&.Mui-focused": { color: designTokens.primary.fixedDim } },
                                        "& .MuiSvgIcon-root": { color: sidebarColors.text },
                                    }}
                                    renderInput={(params) => (
                                        <Tooltip title={property?.propertyName || ""} arrow placement="right">
                                            <TextField {...params} fullWidth label="Property" size="small" />
                                        </Tooltip>
                                    )}
                                />
                            </Grid>
                        </Box>

                        {/* Nav Items */}
                        <Box sx={{ flex: 1, overflow: "auto", py: 1 }}>
                            <List sx={{ py: 0 }}>
                                {navItems.map(({ text, icon }) => {
                                    if (!hasViewPermission(text)) return null;
                                    const lcText = text.toLowerCase().replace(/\s+/g, "-");
                                    const isActive = active === lcText;

                                    return (
                                        <ListItem key={text} disablePadding>
                                            <ListItemButton
                                                onClick={() => { navigate(`/${lcText}`); setActive(lcText); }}
                                                sx={{
                                                    py: 0.8, px: 2.5,
                                                    borderLeft: isActive ? `3px solid ${designTokens.primary.fixedDim}` : "3px solid transparent",
                                                    backgroundColor: isActive ? sidebarColors.bgActive : "transparent",
                                                    color: isActive ? sidebarColors.textActive : sidebarColors.text,
                                                    "&:hover": {
                                                        backgroundColor: isActive ? sidebarColors.bgActive : sidebarColors.bgHover,
                                                        color: sidebarColors.textActive,
                                                    },
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 32, color: isActive ? "#fff" : sidebarColors.text, "& svg": { fontSize: "1.1rem" } }}>
                                                    {icon}
                                                </ListItemIcon>
                                                <ListItemText primary={text} primaryTypographyProps={{ fontSize: "0.8125rem", fontWeight: isActive ? 600 : 400 }} />
                                                {isActive && <ChevronRightOutlined sx={{ fontSize: "1rem", color: "#fff" }} />}
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </Box>

                        {/* Powered by */}
                        <Box sx={{ p: "12px 20px", borderTop: "1px solid rgba(255,255,255,0.08)", textAlign: "center" }}>
                            <Typography sx={{ fontSize: "0.6rem", color: sidebarColors.textMuted, letterSpacing: "0.03em" }}>
                                Powered by{" "}
                                <a href="https://smartflix.ae/" target="_blank" rel="noopener noreferrer"
                                    style={{ color: designTokens.primary.fixedDim, textDecoration: "none", fontWeight: 600 }}
                                >
                                    Smartflix Technologies
                                </a>
                            </Typography>
                        </Box>
                    </Box>
                </Drawer>
            )}
        </Box>
    );
};
export default Sidebar;
